import ReactPDF, {Font, StyleSheet} from '@react-pdf/renderer';
import {DEFAULT_SEARCH_RADIUS} from '../Constants/SearchRadius';
import {IGenericNumberObject, IGenericStringArrayObject} from '../Types/IGenericObject';
import {ISearchFilterSet} from '../Types/ISearchFilter';
import SearchFilterName from '../Enum/SearchFilterName';
import {IResultsData, IResultSummary} from '../Types/IResultsData';
import {IDepotInfo, IDepotMap, IDepotSummary} from '../Types/IDepot';
import {getSupplierName} from './SupplierUtils';
import {ActiveReportFilters} from '../Enum/ActiveReportFilters';

const hyphenationCallback = (word) => [word];
Font.registerHyphenationCallback(hyphenationCallback);
Font.register({ family: 'DM Sans Regular', src: 'https://fonts.gstatic.com/s/dmsans/v6/rP2Hp2ywxg089UriOZSCHBeHFl0.ttf' });
Font.register({ family: 'DM Sans Bold', src: 'https://fonts.gstatic.com/s/dmsans/v6/rP2Cp2ywxg089UriASitOB-sClQX6Cg.ttf'});
Font.register({ family: 'DM Sans Italic', src: 'https://fonts.gstatic.com/s/dmsans/v6/rP2Fp2ywxg089UriCZaIGDWCBl0O8Q.ttf'});

const reportStyle: ReactPDF.Styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        paddingTop: '18pt',
        paddingLeft: '18pt',
        paddingRight: '18pt',
        paddingBottom: 56,
        flex: 1,
        fontFamily: 'DM Sans Regular',
        fontSize: 13
    },
    textItalic: {
        fontFamily: 'DM Sans Italic'
    },
    fontXl: {
        fontSize: 16,
    },
    fontXs: {
        fontSize: 9
    },
    fontXxs: {
        fontSize: 8
    },
    row: {
        flexDirection: 'row'
    },
    wFull: {
        width: '100%'
    },
    wHalf: {
        width: '50%'
    },
    w3: {
        width: '33.3333%'
    },
    mb: {
        marginBottom: 24
    },
    mt: {
        marginTop: 24
    },
    my4: {
       marginTop: 4,
       marginBottom: 4
    },
    pr: {
        paddingRight: 12
    },
    fontBold: {
        fontFamily: 'DM Sans Bold'
    },
    reportHeader: {
        padding: 20,
        backgroundColor: '#D8E3F2'
    },
    reportHeaderTitle: {
        color: '#1A1A1A',
        fontSize: 24,
        fontWeight: 'bold',
        fontFamily: 'DM Sans Bold'
    },
    reportHeaderDate: {
        marginBottom: 8,
        marginTop: 8
    },
    reportSummary: {
        marginTop: 25,
        marginBottom: 50
    },
    reportSummaryHeader: {
        fontSize: 16,
        fontFamily: 'DM Sans Bold',
        marginBottom: 24
    },
    reportSummaryDetails: {
        flexDirection: 'row'
    },
    reportSummaryLeftCol: {
        width: '60%',
        paddingRight: 16
    },
    reportSummaryRightCol: {
        width: '40%',
        padding: 24,
        backgroundColor: '#F0F5FC'
    },
    numberCol: {
        width: '4%',
        paddingTop: 8,
        paddingBottom: 8
    },
    perDayCol: {
        width: '11%',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    durationCol: {
        width: '15%',
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    modelCol: {
        width: '20%',
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    supplierCol: {
        width: '10%',
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    seatsCol: {
        width: '10%',
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    depotCol: {
        width: '30%',
        paddingRight: 10,
        paddingTop: 8,
        paddingBottom: 8
    },
    statementNumCol: {
        width: 24,
    },
    statementContentCol: {
        width: '95%'
    },
    depotSummaryCol: {
        width: '20%'
    },
    tableHeader: {
        backgroundColor: '#DBDDE8',
        fontSize: 7
    },
    depotSummaryTableCol: {
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 6,
        paddingBottom: 6,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex'
    },
    depotSummaryTableColContainer: {
        alignSelf: 'center',
        width: '100%'
    },
    depotSummaryTableColIdx: {
        width: '3%'
    },
    depotSummaryTableColDistance: {
        width: '17%',
        paddingLeft: 6
    },
    depotSummaryTableColPrice: {
        width: '20%'
    },
    depotSummaryTableColSupplier: {
        width: '15%'
    },
    depotSummaryTableColAddress: {
        width: '25%'
    },
    depotSummaryIndexNumber: {
        width: 14,
        height: 14,
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
        borderBottomRightRadius: 7,
        borderBottomLeftRadius: 7,
        backgroundColor: '#898989',
        color: '#FFFFFF',
        justifyContent: 'center',
        alignItems: 'center'
    },
    depotSummaryPinIconContainer: {
        maxWidth: 25,
        width: '10%',
        textAlign: 'center',
        color: '#ffffff',
        fontSize: 14,
        position: 'relative',
        paddingRight: 5
    },
    copyrightText: {
        fontSize: 7,
        color: '#898989',
        marginBottom: 5,
        textAlign: 'left'
    },
    selectedText: {
        backgroundColor: '#00987E',
        color: '#fff',
        borderRadius: 5,
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '35%',
        fontSize: 9
    },
    dot: {
        width: 4,
        height: 4,
        display: 'flex',
        marginRight: 10,
        marginLeft: 10,
        borderRadius: 2,
        marginTop: 3,
        backgroundColor: '#333333'
    },
    aboutPageHeaderText: {
        color: '#1A1A1A',
        fontSize: '19pt'
    },
    aboutPageBodyText: {
        color: '#333333',
        fontSize: '8pt'
    },
    summarySectionTitle: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 3,
        paddingBottom: 3,
        borderRadius: 3,
        fontSize: 9,
    },
    inline: {
        display: 'flex',
        flexDirection: 'row'
    }
});

export default reportStyle;


export const ancillaryPricingStyles: ReactPDF.Styles = StyleSheet.create({
   leftHeader: {
       width: '7.5%',
       display: 'flex',
       alignItems: 'stretch',
       flexGrow: 1,
       paddingLeft: 1,
       paddingTop: 1,
       paddingRight: 2,
       paddingBottom: 1
   },
   baseTd: {
       padding: 1.5,
       display: 'flex',
       alignItems: 'stretch',
       flexGrow: 1,
   },
   col: {
       width: '7.5%',
       padding: 0.5,
       display: 'flex',
       alignItems: 'stretch',
       flexGrow: 1
   },
   heading: {
       fontSize: 16
   },
   tableTitle: {
       fontSize: 10,
       padding: 5,
       textAlign: 'center',
       backgroundColor: '#F0F5FC',
       marginBottom: 5,
       marginTop: 10
   }
});

export const excessReductionProductsStyles: ReactPDF.Styles = StyleSheet.create({
    heading: {
        fontSize: 16
    },
    subHeading: {
        fontSize: 12
    },
    grayText: {
        color: '#4D4D4D'
    },
    col: {
        paddingTop: 5,
        paddingLeft: 10,
        paddingRight: 10
    },
    bordered: {
        borderStyle: 'solid',
        borderColor: '#CCCCCC',
    },
    borderLeft: {
      borderLeftWidth: 1,
    },
    borderRight: {
        borderRightWidth: 1,
    },
    borderTop: {
        borderTopWidth: 1,
    },
    borderBottom: {
        borderBottomWidth: 1,
    },
    indentLevel1: {
        paddingLeft: 10,
    },
    indentLevel2: {
        paddingLeft: 20,
    },
    mb: {
        marginBottom: 4
    },
    mbXs: {
        marginBottom: 3
    }
});

export const getRadiusFromFilterSummary = (filtersSummary: IGenericStringArrayObject | null, availableFilters: ISearchFilterSet | null): string => {
    let radius: string = DEFAULT_SEARCH_RADIUS.toFixed() + 'Km';

    if (
        filtersSummary !== null &&
        availableFilters !== null &&
        filtersSummary.hasOwnProperty(availableFilters[SearchFilterName.RADIUS].label)
    ) {
        radius = filtersSummary[availableFilters[SearchFilterName.RADIUS].label][0].toString();
        radius += 'km';
    }

    return radius;
}

export const getClosestAndLowestIndex = (resultSummary: IResultSummary): IGenericNumberObject => {
    const result: IGenericNumberObject = {};

    if (resultSummary.lowestPerDay !== null && resultSummary.closest !== null) {
        for (let i = 0; i < resultSummary.depotSummary.length; i++) {
            const lowestPerDay: IResultsData | null = resultSummary.depotSummary[i].lowestPerDay;
            if (lowestPerDay !== null && lowestPerDay.id === resultSummary.lowestPerDay.id) {
                result.lowest = i;
            }

            if (resultSummary.depotSummary[i].depot_code === resultSummary.closest.depot_code) {
                result.closest = i;
            }

            if (result.hasOwnProperty('closest') && result.hasOwnProperty('lowest')) {
                break;
            }
        }
    }

    return result;
}

export const getDepotsWithNoResults = (depotsAtArea: IDepotInfo[], depotsSummary: IDepotSummary[], radius: number): IDepotSummary[] => {
    const depotSummaryMap: IDepotMap = {};
    const depotsAtAreaMap: IDepotMap = {};
    let results: IDepotSummary[] = [];
    const radiusInKm = radius > 999 ? radius / 1000 : radius;

    depotsSummary.forEach((depot: IDepotSummary) => {
        if (typeof depotSummaryMap[`${depot.depot_code}_${depot.supplier_code}`] === 'undefined') {
            depotSummaryMap[`${depot.depot_code}_${depot.supplier_code}`] = depot;
        }
    });

   // remove duplicates
    depotsAtArea.forEach((depot: IDepotInfo) => {
        if (typeof depotsAtAreaMap[`${depot.code}_${depot.supplier_code}`] === 'undefined') {
            depotsAtAreaMap[`${depot.code}_${depot.supplier_code}`] = {
                depot_code: depot.code,
                distance: parseFloat(depot.distance),
                supplier_code: depot.supplier_code,
                supplier_name: getSupplierName(depot.supplier_code),
                highestPerDay: null,
                lowestPerDay: null,
                address: depot.address,
                depot_coordinates: {
                    x: depot.coordinates.x,
                    y: depot.coordinates.y
                }
            };
        }
    });

   Object.keys(depotsAtAreaMap).forEach((key: string) => {
       if (typeof depotSummaryMap[key] === 'undefined') {
          results.push(depotsAtAreaMap[key]);
       }
   });

   results = results.filter((depot) => {
       return depot.distance < radiusInKm
   })

   return results.sort((a, b) => {
       if (a.distance > b.distance) {
           return 1;
       } else if (b.distance > a.distance) {
           return -1;
       }

       return 0;
   });
}

export const getActiveReportFilters = (activeFilters: ISearchFilterSet | null): ActiveReportFilters[] => {
    const reportFilters: ActiveReportFilters[] = [];

    if (activeFilters === null) {
        return [ActiveReportFilters.DEFAULT];
    }

    Object.keys(activeFilters).forEach((key: string) => {
        if (activeFilters[key].hasSelection === true && key === 'supplier') {
            reportFilters.push(ActiveReportFilters.SUPPLIERS)
        }

        if (activeFilters[key].hasSelection === true && key === 'depot') {
            reportFilters.push(ActiveReportFilters.DEPOTS)
        }

        if (activeFilters[key].hasSelection === true && key === 'carModel') {
            reportFilters.push(ActiveReportFilters.CAR_MODEL)
        }

        if (activeFilters[key].hasSelection === true && key === 'duration') {
            reportFilters.push(ActiveReportFilters.DURATION)
        }

        if (activeFilters[key].hasSelection === true && key === 'radius') {
            reportFilters.push(ActiveReportFilters.RADIUS);
        }
    });

    return reportFilters;
}

export const getDepotAddress = (depotsAtArea: IDepotInfo[], depotCodeAndSupplierCode: string): string => {
   const codes: string[] = depotCodeAndSupplierCode.split('_');
   let result: string = '';

   for (let i: number = 0; i < depotsAtArea.length; i++) {
        if (codes.length < 2) {
            break;
        }

       if (
           depotsAtArea[i].code === codes[0] &&
           depotsAtArea[i].supplier_code === codes[1] &&
           depotsAtArea[i].address !== ''
       ) {
           result = depotsAtArea[i].address;
           break;
       }
   }

   return result;
}

export const getSummaryTitleStyle = (filters: ActiveReportFilters[], key: ActiveReportFilters): {} => {
    const baseStyle = {...reportStyle.summarySectionTitle, ...reportStyle.inline}

    if (filters.indexOf(key) !== -1) {
        return {
            ...baseStyle,
            ...{
                paddingLeft: 3,
                paddingRight: 3,
                backgroundColor: '#00987E',
                color: '#fff'
            }
        }
    }

    return baseStyle;
}

export const getSummaryTitleText = (title: string, filters: ActiveReportFilters[], key: ActiveReportFilters): string => {
    if (filters.indexOf(key) !== -1) {
        return `SELECTED ${title}`;
    }

    return title;
}
